import { useState, useEffect } from "react";
import { Nav, Avatar, Button, ButtonToolbar } from "rsuite";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAccess } from "@hooks/useAccess";
import { authService } from "@services/authService";
import IconsList from "@components/IconsList";
import { brand } from "@/assets/theme/colors";

interface SidebarProps {
  userName?: string;
}

const Sidebar = ({ userName }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeKey, setActiveKey] = useState(location.pathname);
  const { canAccess } = useAccess();
  const [user, setUser] = useState<{
    email?: string;
    name?: string;
    role?: string;
  } | null>(null);

  // Obtener datos del usuario del servicio de autenticación
  useEffect(() => {
    const user = authService.getUser();
    if (user) {
      setUser(user);
    }
  }, []);

  // Mantener activo sincronizado con la ruta actual
  useEffect(() => {
    setActiveKey(location.pathname);
  }, [location.pathname]);

  // Menú completo - se filtrará según permisos
  const allMenuItems = [
    { key: "/dashboard", label: "Inicio", icon: "inicio" },
    { key: "/recursos", label: "Recursos", icon: "recursos" },
    { key: "/productos", label: "Productos", icon: "productos" },
    {
      key: "/productos-profesionales",
      label: "Productos Profesionales",
      icon: "precios",
    },
    { key: "/clientes", label: "Clientes", icon: "clientes" },
    { key: "/profesionales", label: "Profesionales", icon: "profesionales" },
    { key: "/pedidos", label: "Pedidos", icon: "pedidos" },
    { key: "/estadisticas", label: "Estadísticas", icon: "estadisticas" },
  ];

  // Filtrar menú según permisos
  const menuItems = allMenuItems.filter((item) => canAccess(item.key));

  const handleLogout = async () => {
    try {
      // Usar el servicio de autenticación para logout
      await authService.logout();
      setUser(null);

      // Redirigir al login
      navigate("/login");
    } catch (error) {
      // Redirigir al login incluso si hay error
      navigate("/login");
    }
  };

  return (
    <div className="sidebar-container">
      {/* Logo y Header */}
      <div className="sidebar-header">
        <div className="logo-container">
          <img
            src="/logoPescetti.jpg"
            alt="Pescetti"
            className="sidebar-logo"
          />
          <div className="logo-text">
            <h2>Pescetti</h2>
            <p>Sistema ERP</p>
          </div>
        </div>
      </div>

      {/* Menú Principal */}
      <div className="sidebar-menu">
        <Nav
          vertical
          activeKey={activeKey}
          onSelect={setActiveKey}
          className="main-nav"
        >
          {menuItems
            .filter((item) => canAccess(item.key))
            .map((item) => {
              const IconComponent = IconsList[item.icon];
              const isActive = activeKey === item.key;

              return (
                <Nav.Item
                  key={item.key}
                  as={Link}
                  to={item.key}
                  icon={
                    IconComponent ? (
                      <IconComponent style={{ color: "#ffffff" }} />
                    ) : undefined
                  }
                  className="nav-item"
                  style={{
                    backgroundColor: isActive ? brand.orange : "transparent",
                    color: "#ffffff",
                    borderRadius: 6,
                  }}
                >
                  {item.label}
                </Nav.Item>
              );
            })}
        </Nav>
      </div>

      {/* Área de Usuario */}
      <div className="sidebar-footer">
        <div className="user-section">
          <div className="user-info">
            <Avatar
              circle
              style={{
                backgroundColor: "#823DC2",
                color: "white",
                fontWeight: "bold",
              }}
            >
              {(user?.name || user?.email || userName || "U")
                .charAt(0)
                .toUpperCase()}
            </Avatar>
            <div className="user-details">
              <div className="user-name-row">
                <span className="user-name">
                  {user?.name || user?.email || userName || "Usuario"}
                </span>
                <ButtonToolbar className="user-actions">
                  {/* Configuración visible para ADMIN, DEV y OWNER */}
                  {(user?.role === "DEV" ||
                    user?.role === "OWNER" ||
                    user?.role === "ADMIN") && (
                    <Button
                      as={Link}
                      to="/configuracion"
                      appearance="ghost"
                      size="sm"
                      className="action-btn"
                    >
                      <IconsList.configuracion />
                    </Button>
                  )}
                  <Button
                    appearance="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="action-btn logout-btn"
                  >
                    <IconsList.logout />
                  </Button>
                </ButtonToolbar>
              </div>
              {user && "role" in user && (
                <span className="user-role">
                  {user.role === "ADMIN"
                    ? "Usuario"
                    : user.role === "OWNER"
                    ? "Administrador"
                    : user.role === "DEV"
                    ? "Desarrollador"
                    : "Observador"}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
